import { getIncidentTypeLabel } from "@/lib/utils/matters/incident-classification";
import { AddOrEditDefaultMatterExpenseModal } from "@/components/features/settings/defaults/matter-default-expenses/AddOrEditDefaultMatterExpenseModal";
import { MatterDefaultExpenseRowContextMenu } from "@/components/features/settings/defaults/matter-default-expenses/MatterDefaultExpenseRowContextMenu";
import {
  MattersStyleTableShell,
  useMattersStyleTableColumnDragSensors,
} from "@/components/features/matters/matters-style-table/MattersStyleTableShell";
import type { MattersStyleColumnMeta } from "@/components/features/matters/matters-style-table/MattersStyleDraggableHeader";
import {
  ActionConfirmationModal,
  CurrencyWrapper,
  Text,
} from "@/components/ui";
import { useUser } from "@/components/UserProvider";
import { retrieveMatterSettingsQueryOptions } from "@/lib/data/react-query/matters/matter-settings";
import { useDeleteMatterDefaultExpense } from "@/lib/data/react-query/matters/matter-default-expenses";
import { serverErrorFallbackText } from "@/lib/data/static/fallbackMessages";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { tanstackTableNames } from "@/lib/data/static/tanstack-table-names";
import type { DefaultMatterExpense } from "@/lib/types";
import {
  persistTanstackTablePrefs,
  readTanstackTablePrefs,
} from "@/lib/utils/tanstack-table-local-storage";
import { MultiValueCell } from "@/lib/utils/tableUtils";
import { arrayMove } from "@dnd-kit/sortable";
import type { DragEndEvent } from "@dnd-kit/core";
import { ErrorBoundary } from "@sentry/react";
import {
  type ColumnDef,
  type SortingState,
  type VisibilityState,
  functionalUpdate,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { isNil } from "lodash";
import { useMemo, useState } from "react";

/** Column ids aligned with DefaultMatterExpenseSchema + AddOrEditDefaultMatterExpenseModal. */
const COST_TABLE_COLUMN_IDS = [
  "Label",
  "Category",
  "Incident types",
  "Cost",
] as const;

type MatterExpenseRow = DefaultMatterExpense & { incidentType: string[] };

/**
 * Search against cost: full text (raw + grouped + optional decimals) and digit-only
 * substring match so `235`, `123`, `123,123,235`, and `123123235` all behave as expected.
 */
function defaultMatterExpenseCostMatchesQuery(
  cost: number | null,
  q: string,
): boolean {
  if (cost == null || Number.isNaN(cost)) return false;
  const ql = q.trim().toLowerCase();
  if (!ql) return true;

  const n = Number(cost);

  const textHaystacks = [
    String(n),
    n.toLocaleString("en-US"),
    n.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }),
  ].map((s) => s.toLowerCase());

  if (textHaystacks.some((h) => h.includes(ql))) return true;

  const qDigits = ql.replace(/\D/g, "");
  if (qDigits.length === 0) return false;

  const digitsFromRaw = String(n).replace(/\D/g, "");
  const digitsFromGrouped = n
    .toLocaleString("en-US", { maximumFractionDigits: 2 })
    .replace(/\D/g, "");

  return digitsFromRaw.includes(qDigits) || digitsFromGrouped.includes(qDigits);
}

const MATTER_DEFAULT_EXPENSES_TABLE_LS_KEY: string =
  tanstackTableNames.matterDefaultExpenses;

function MatterDefaultExpensesTableDangerous({
  isAddingExpense,
  setIsAddingExpense,
}: {
  isAddingExpense: boolean;
  setIsAddingExpense: (open: boolean) => void;
}) {
  const [isEditingExpense, setIsEditingExpense] = useState(false);
  const [activeDefaultMatterExpenseId, setActiveDefaultMatterExpenseId] =
    useState<string | null>(null);
  const [expenseToDelete, setExpenseToDelete] =
    useState<DefaultMatterExpense | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  const [sorting, setSorting] = useState<SortingState>(() => {
    const stored = readTanstackTablePrefs(MATTER_DEFAULT_EXPENSES_TABLE_LS_KEY);
    return stored.sorting ?? [];
  });
  const [pagination, setPagination] = useState(() => {
    const stored = readTanstackTablePrefs(MATTER_DEFAULT_EXPENSES_TABLE_LS_KEY);
    return {
      pageIndex: 0,
      pageSize: stored.pagination?.pageSize ?? 10,
    };
  });
  const [columnOrder, setColumnOrder] = useState<string[]>(() => {
    const stored = readTanstackTablePrefs(MATTER_DEFAULT_EXPENSES_TABLE_LS_KEY);
    const ids = [...COST_TABLE_COLUMN_IDS];
    const order = stored.columnOrder;
    if (order?.length) {
      return [...order, ...ids.filter((id) => !order.includes(id))];
    }
    return ids;
  });
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => {
      const stored = readTanstackTablePrefs(
        MATTER_DEFAULT_EXPENSES_TABLE_LS_KEY,
      );
      return {
        ...stored.columnVisibility,
        Label: true,
      };
    },
  );

  const { hasActionPermission } = useUser();
  const canEditExpenses = hasActionPermission(
    ACTION_PERMISSION_KEYS.defaultMatterExpense,
    ACTION_PERMISSION_VALUES.update,
  );
  const canDeleteExpenses = hasActionPermission(
    ACTION_PERMISSION_KEYS.defaultMatterExpense,
    ACTION_PERMISSION_VALUES.delete,
  );
  const deleteMatterDefaultExpense = useDeleteMatterDefaultExpense();

  const {
    data: matterSettingsData,
    isLoading: isMatterSettingsLoading,
    isError: isMatterSettingsError,
  } = useQuery(retrieveMatterSettingsQueryOptions());
  const incidentTypes = useMemo(
    () => matterSettingsData?.data.incidentTypes ?? [],
    [matterSettingsData],
  );
  const matterDefaultExpenses = useMemo(
    () => matterSettingsData?.data.defaultExpenses ?? [],
    [matterSettingsData],
  );
  const rows = useMemo(
    () =>
      matterDefaultExpenses.map((matterDefaultExpense) => ({
        ...matterDefaultExpense,
        incidentType: incidentTypes
          .filter((type) =>
            matterDefaultExpense.incidentTypeIds.includes(type.id),
          )
          .map((type) => getIncidentTypeLabel(type)),
      })),
    [matterDefaultExpenses, incidentTypes],
  );

  const filteredRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => {
      const label = row.label.toLowerCase();
      const category = row.category.toLowerCase();
      const incidentTypesStr = row.incidentType.join(" ").toLowerCase();
      const desc = (row.description ?? "").toLowerCase();
      return (
        label.includes(q) ||
        category.includes(q) ||
        incidentTypesStr.includes(q) ||
        desc.includes(q) ||
        defaultMatterExpenseCostMatchesQuery(row.cost, q)
      );
    });
  }, [rows, searchQuery]);

  const columns = useMemo<ColumnDef<MatterExpenseRow>[]>(
    () => [
      {
        id: "Label",
        accessorFn: (row) => row.label,
        header: "Label",
        enableHiding: false,
        cell: ({ row }) => row.original.label,
        sortingFn: "alphanumeric",
      },
      {
        id: "Category",
        accessorFn: (row) => row.category,
        header: "Category",
        cell: ({ row }) => row.original.category,
        sortingFn: "alphanumeric",
      },
      {
        id: "Incident types",
        accessorFn: (row) => row.incidentType.join(", "),
        header: "Incident types",
        cell: ({ row }) => (
          <MultiValueCell value={row.original.incidentType} showItemCount={2} />
        ),
        sortingFn: "alphanumeric",
      },
      {
        id: "Cost",
        accessorFn: (row) => row.cost,
        header: "Cost",
        meta: {
          cellClassName: "text-right tabular-nums",
          headerAlign: "end",
        } satisfies MattersStyleColumnMeta,
        cell: ({ row }) => (
          <div className="flex justify-end">
            <CurrencyWrapper value={row.original.cost ?? undefined} />
          </div>
        ),
        sortingFn: "basic",
      },
    ],
    [],
  );

  const table = useReactTable({
    data: filteredRows,
    columns,
    autoResetPageIndex: true,
    state: {
      sorting,
      columnOrder,
      columnVisibility,
      pagination,
    },
    onSortingChange: (updater) => {
      setSorting((prev) => {
        const next = functionalUpdate(updater, prev);
        persistTanstackTablePrefs(MATTER_DEFAULT_EXPENSES_TABLE_LS_KEY, {
          sorting: next,
        });
        return next;
      });
    },
    onPaginationChange: (updater) => {
      setPagination((prev) => {
        const next = functionalUpdate(updater, prev);
        persistTanstackTablePrefs(MATTER_DEFAULT_EXPENSES_TABLE_LS_KEY, {
          pagination: { pageSize: next.pageSize },
        });
        return next;
      });
    },
    onColumnOrderChange: (updater) => {
      setColumnOrder((prev) => {
        const next = functionalUpdate(updater, prev);
        persistTanstackTablePrefs(MATTER_DEFAULT_EXPENSES_TABLE_LS_KEY, {
          columnOrder: next,
        });
        return next;
      });
    },
    onColumnVisibilityChange: (updater) => {
      setColumnVisibility((prev) => {
        const next = functionalUpdate(updater, prev);
        const merged = { ...next, Label: true };
        persistTanstackTablePrefs(MATTER_DEFAULT_EXPENSES_TABLE_LS_KEY, {
          columnVisibility: merged,
        });
        return merged;
      });
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    sortingFns: {
      customDateTime: (rowA, rowB, columnId) => {
        if (isNil(rowA.getValue(columnId))) {
          return 1;
        }
        if (isNil(rowB.getValue(columnId))) {
          return -1;
        }
        return dayjs(rowA.getValue(columnId)).diff(
          dayjs(rowB.getValue(columnId)),
        );
      },
    },
  });

  const sensors = useMattersStyleTableColumnDragSensors();
  const activeDefaultMatterExpense = matterDefaultExpenses.find(
    (expense) => expense.id === activeDefaultMatterExpenseId,
  );

  const handleColumnDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setColumnOrder((prev) => {
      const oldIndex = prev.indexOf(active.id as string);
      const newIndex = prev.indexOf(over.id as string);
      if (oldIndex < 0 || newIndex < 0) return prev;
      const next = arrayMove(prev, oldIndex, newIndex);
      persistTanstackTablePrefs(MATTER_DEFAULT_EXPENSES_TABLE_LS_KEY, {
        columnOrder: next,
      });
      return next;
    });
  };

  const handleDeleteExpense = async () => {
    if (!expenseToDelete) return;

    await deleteMatterDefaultExpense.mutateAsync({ id: expenseToDelete.id });
    setExpenseToDelete(null);
  };

  return (
    <div>
      <MattersStyleTableShell
        table={table}
        columnOrder={columnOrder}
        columnIdCountBaseline={COST_TABLE_COLUMN_IDS.length}
        onColumnDragEnd={handleColumnDragEnd}
        sensors={sensors}
        search={{
          value: searchQuery,
          onChange: (value) => {
            setSearchQuery(value);
            setPagination((p) => ({ ...p, pageIndex: 0 }));
          },
          placeholder: "Search categories...",
        }}
        isLoading={isMatterSettingsLoading}
        isError={isMatterSettingsError}
        errorMessage={serverErrorFallbackText}
        emptyMessage="No categories found."
        totalFilteredRowCount={filteredRows.length}
        entityNamePlural="categories"
        sorting={sorting}
        tableProps={{
          "data-testid": tanstackTableNames.matterDefaultExpenses,
          "data-table-name": tanstackTableNames.matterDefaultExpenses,
        }}
        getTableRowProps={(row) => ({
          "data-testid": "table-row",
          className: "cursor-pointer",
          onClick: () => {
            setActiveDefaultMatterExpenseId(row.original.id);
            setIsEditingExpense(true);
          },
        })}
        renderContextMenu={(row, trigger) => (
          <MatterDefaultExpenseRowContextMenu
            expense={row.original}
            trigger={trigger}
            canEdit={canEditExpenses}
            canDelete={canDeleteExpenses}
            onEdit={() => {
              setActiveDefaultMatterExpenseId(row.original.id);
              setIsEditingExpense(true);
            }}
            onDelete={() => {
              setExpenseToDelete(row.original);
            }}
          />
        )}
      />

      {activeDefaultMatterExpenseId && activeDefaultMatterExpense ? (
        <AddOrEditDefaultMatterExpenseModal
          editing={true}
          activeDefaultMatterExpense={activeDefaultMatterExpense}
          setOpen={setIsEditingExpense}
          open={isEditingExpense}
          isReadOnly={!canEditExpenses}
        />
      ) : null}

      <AddOrEditDefaultMatterExpenseModal
        editing={false}
        open={isAddingExpense}
        setOpen={setIsAddingExpense}
      />

      <ActionConfirmationModal
        open={Boolean(expenseToDelete)}
        setOpen={(open) => {
          if (!open) setExpenseToDelete(null);
        }}
        title="Delete cost category"
        description={`Are you sure you want to delete "${expenseToDelete?.label}"? This action cannot be undone.`}
        buttonText="Delete"
        afterActionFn={handleDeleteExpense}
      />
    </div>
  );
}

export function MatterDefaultExpensesTable({
  isAddingExpense,
  setIsAddingExpense,
}: {
  isAddingExpense: boolean;
  setIsAddingExpense: (open: boolean) => void;
}) {
  return (
    <ErrorBoundary
      fallback={<Text variant="subHeading">{serverErrorFallbackText}</Text>}
    >
      <MatterDefaultExpensesTableDangerous
        isAddingExpense={isAddingExpense}
        setIsAddingExpense={setIsAddingExpense}
      />
    </ErrorBoundary>
  );
}
